/**
 * <s-sticky-buy>: mobile bottom bar with a quantity stepper and "Add to cart · $X.XX".
 *
 * It does not add to cart itself. It mirrors and edits the main product form's quantity input
 * (input[name="quantity"]) and submits that form through its own Add to cart button
 * (button[name="add"]), so <s-product-form> keeps handling the cart and drawer exactly as before.
 *
 * Attributes:
 *   data-price    - Variant price in cents.
 *   data-currency - ISO currency code for formatting.
 *   data-locale   - Locale for number formatting.
 *   data-label    - Button label with a [PRICE] placeholder.
 *   data-visible  - Set while the main Add to cart button is off screen.
 */
class StickyBuy extends HTMLElement {
  #observer = null;
  #abort = null;

  get #form() {
    return this.closest('s-product-form')?.querySelector('form[action*="/cart/add"]') ?? null;
  }

  get #qtyInput() {
    return /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="quantity"]') ?? null);
  }

  get #mainAdd() {
    return /** @type {HTMLButtonElement | null} */ (this.#form?.querySelector('button[name="add"]') ?? null);
  }

  get #quantity() {
    return Math.max(1, Number.parseInt(this.#qtyInput?.value || '1', 10) || 1);
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        if (target.closest('[data-sticky-minus]')) this.#setQuantity(this.#quantity - this.#step);
        else if (target.closest('[data-sticky-plus]')) this.#setQuantity(this.#quantity + this.#step);
        else if (target.closest('[data-sticky-add]')) this.#submit();
      },
      { signal },
    );

    // Keep the bar in sync when the shopper uses the main stepper instead.
    this.#form?.addEventListener('input', () => this.#render(), { signal });
    this.#form?.addEventListener('change', () => this.#render(), { signal });

    const mainAdd = this.#mainAdd;
    if (mainAdd) {
      this.#observer = new IntersectionObserver(([entry]) => {
        const offScreen = !entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight;
        this.toggleAttribute('data-visible', offScreen);
      });
      this.#observer.observe(mainAdd);
    }

    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #step() {
    return Math.max(1, Number.parseInt(this.#qtyInput?.step || '1', 10) || 1);
  }

  /** @param {number} value */
  #setQuantity(value) {
    const input = this.#qtyInput;
    if (!input) return;
    const min = Number.parseInt(input.min || '1', 10) || 1;
    const max = input.max ? Number.parseInt(input.max, 10) : Infinity;
    input.value = String(Math.min(max, Math.max(min, value)));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.#render();
  }

  #submit() {
    const form = this.#form;
    const mainAdd = this.#mainAdd;
    if (!form || !mainAdd) return;
    form.requestSubmit(mainAdd);
  }

  #render() {
    const qty = this.#quantity;
    const qtyText = this.querySelector('[data-sticky-qty]');
    if (qtyText) qtyText.textContent = String(qty);

    const cents = Number(this.dataset.price || 0) * qty;
    let price = '';
    try {
      price = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch {
      price = (cents / 100).toFixed(2);
    }
    const button = this.querySelector('[data-sticky-add]');
    if (button) button.textContent = (this.dataset.label || '[PRICE]').replace('[PRICE]', price);
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', StickyBuy);
